import React, { useEffect, useState } from "react";
import { adminAxios } from "../api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";

const DEFAULTS = {
  enabled: true,
  autoOpen: true,
  autoDelaySeconds: 4,
  reappearMinutes: 0,
  whatsappBadge: true,
  phone: "+919399993389",
  phoneDisplay: "+91 93999 93389",
  whatsapp: "919399993389",
  whatsappMessage: "Hi, I'm interested in the Adrusta Ratnam. Please share the details.",
  heading: "Have a Question Before You Order?",
  subtext: "Our team is one call away. Speak to us & reserve your energised Ratnam today.",
  offerEnabled: true,
  offerText: "Limited-time: Call now for an EXTRA 10% OFF — today only!",
  greetEnabled: true,
  greetText: "We're online — chat now!",
  greetDelaySeconds: 6,
  leadEnabled: true,
  leadTitle: "Or leave your number & we'll call you back",
};

const Row = ({ label, hint, children }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
    <div className="min-w-0">
      <div className="text-sm font-medium text-slate-800">{label}</div>
      {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
    </div>
    <div className="shrink-0 w-[220px]">{children}</div>
  </div>
);

const Group = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-1">{title}</h3>
    <div>{children}</div>
  </div>
);

const CallusForm = () => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const doc = (await adminAxios.get("/cms/callus")).data;
      setF({ ...DEFAULTS, ...(doc?.data || {}) });
    } catch {
      setF({ ...DEFAULTS });
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await adminAxios.put("/cms/callus", { section: "callus", data: f, published: true });
      toast.success("Popup settings updated — live on website");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (!f) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-semibold text-slate-800">Call Us Popup</h2>
          <p className="text-xs text-slate-400">Control the promotional popup, call/WhatsApp buttons and chat greeting.</p>
        </div>
        <Button data-testid="callus-save-btn" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save &amp; Publish
        </Button>
      </div>

      <Group title="Visibility & Timing">
        <Row label="Show the popup" hint="Turn the whole Call Us popup on or off">
          <div className="flex justify-end"><Switch data-testid="callus-enabled" checked={f.enabled} onCheckedChange={(v) => set("enabled", v)} /></div>
        </Row>
        <Row label="Open automatically" hint="Pop up on its own after a delay">
          <div className="flex justify-end"><Switch data-testid="callus-autoopen" checked={f.autoOpen} onCheckedChange={(v) => set("autoOpen", v)} /></div>
        </Row>
        <Row label="Open after" hint="How long to wait before it appears">
          <Select value={String(f.autoDelaySeconds)} onValueChange={(v) => set("autoDelaySeconds", Number(v))}>
            <SelectTrigger data-testid="callus-delay"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2 seconds</SelectItem>
              <SelectItem value="4">4 seconds</SelectItem>
              <SelectItem value="6">6 seconds</SelectItem>
              <SelectItem value="8">8 seconds</SelectItem>
              <SelectItem value="10">10 seconds</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <Row label="Show again" hint="How often it reappears for the same visitor">
          <Select value={String(f.reappearMinutes)} onValueChange={(v) => set("reappearMinutes", Number(v))}>
            <SelectTrigger data-testid="callus-reappear"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Once per visit</SelectItem>
              <SelectItem value="5">Every 5 minutes</SelectItem>
              <SelectItem value="15">Every 15 minutes</SelectItem>
              <SelectItem value="30">Every 30 minutes</SelectItem>
              <SelectItem value="60">Every hour</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      </Group>

      <Group title="Popup Content">
        <Row label="Heading">
          <Input data-testid="callus-heading" value={f.heading} onChange={(e) => set("heading", e.target.value)} />
        </Row>
        <Row label="Sub text">
          <Textarea data-testid="callus-subtext" rows={3} value={f.subtext} onChange={(e) => set("subtext", e.target.value)} />
        </Row>
      </Group>

      <Group title="Offer Banner">
        <Row label="Show offer banner" hint="A highlighted line to push customers to order">
          <div className="flex justify-end"><Switch data-testid="callus-offer-enabled" checked={f.offerEnabled} onCheckedChange={(v) => set("offerEnabled", v)} /></div>
        </Row>
        <Row label="Offer text">
          <Input data-testid="callus-offer-text" value={f.offerText} onChange={(e) => set("offerText", e.target.value)} />
        </Row>
      </Group>

      <Group title="Call & WhatsApp">
        <Row label="Phone (dial number)" hint="Used by the Call button, e.g. +919399993389">
          <Input data-testid="callus-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
        </Row>
        <Row label="Phone (shown text)">
          <Input data-testid="callus-phone-display" value={f.phoneDisplay} onChange={(e) => set("phoneDisplay", e.target.value)} />
        </Row>
        <Row label="WhatsApp number" hint="With country code, no + or spaces, e.g. 919399993389">
          <Input data-testid="callus-whatsapp" value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
        </Row>
        <Row label="WhatsApp message" hint="Pre-filled message when a customer taps WhatsApp">
          <Textarea data-testid="callus-wa-message" rows={3} value={f.whatsappMessage} onChange={(e) => set("whatsappMessage", e.target.value)} />
        </Row>
        <Row label="Unread badge" hint="Show a pulsing '1' badge on the WhatsApp chip">
          <div className="flex justify-end"><Switch data-testid="callus-wa-badge" checked={f.whatsappBadge} onCheckedChange={(v) => set("whatsappBadge", v)} /></div>
        </Row>
      </Group>

      <Group title="Chat Greeting Bubble">
        <Row label="Show greeting bubble" hint="A small chat bubble above the WhatsApp chip">
          <div className="flex justify-end"><Switch data-testid="callus-greet-enabled" checked={f.greetEnabled} onCheckedChange={(v) => set("greetEnabled", v)} /></div>
        </Row>
        <Row label="Greeting text">
          <Input data-testid="callus-greet-text" value={f.greetText} onChange={(e) => set("greetText", e.target.value)} />
        </Row>
        <Row label="Show after" hint="Delay before the greeting appears">
          <Select value={String(f.greetDelaySeconds)} onValueChange={(v) => set("greetDelaySeconds", Number(v))}>
            <SelectTrigger data-testid="callus-greet-delay"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 seconds</SelectItem>
              <SelectItem value="5">5 seconds</SelectItem>
              <SelectItem value="6">6 seconds</SelectItem>
              <SelectItem value="8">8 seconds</SelectItem>
              <SelectItem value="12">12 seconds</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      </Group>

      <Group title="Lead Capture">
        <Row label="Show 'Leave your number'" hint="Collect a callback number when customers don't call">
          <div className="flex justify-end"><Switch data-testid="callus-lead-enabled" checked={f.leadEnabled} onCheckedChange={(v) => set("leadEnabled", v)} /></div>
        </Row>
        <Row label="Lead prompt text">
          <Input data-testid="callus-lead-title" value={f.leadTitle} onChange={(e) => set("leadTitle", e.target.value)} />
        </Row>
      </Group>
    </div>
  );
};

export default CallusForm;
